import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, GripVertical, CheckCircle2, Save } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

export default function CreateQuiz({ user }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quizData, setQuizData] = useState({
    title: "",
    description: "",
    lesson_index: null,
    passing_score: 70,
    time_limit_minutes: null,
    questions: [
      {
        question: "",
        options: ["", "", "", ""],
        correct_answer: 0,
        points: 1
      }
    ]
  });

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCourse(data);
      }
    } catch (error) {
      toast.error("Failed to load course");
    }
  };

  const handleAddQuestion = () => {
    setQuizData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          question: "",
          options: ["", "", "", ""],
          correct_answer: 0,
          points: 1
        }
      ]
    }));
  };

  const handleRemoveQuestion = (index) => {
    if (quizData.questions.length <= 1) {
      toast.error("Quiz must have at least one question");
      return;
    }
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    setQuizData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === qIndex 
          ? { ...q, options: q.options.map((opt, j) => j === oIndex ? value : opt) }
          : q
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!quizData.title.trim()) {
      toast.error("Please enter a quiz title");
      return;
    }
    
    for (let i = 0; i < quizData.questions.length; i++) {
      const q = quizData.questions[i];
      if (!q.question.trim()) {
        toast.error(`Question ${i + 1} is empty`);
        return;
      }
      const filledOptions = q.options.filter(o => o.trim());
      if (filledOptions.length < 2) {
        toast.error(`Question ${i + 1} needs at least 2 options`);
        return;
      }
      if (!q.options[q.correct_answer]?.trim()) {
        toast.error(`Question ${i + 1}: The correct answer option is empty`);
        return;
      }
    }
    
    setLoading(true);
    
    try {
      // Filter out empty options
      const cleanedQuestions = quizData.questions.map(q => ({
        ...q,
        options: q.options.filter(o => o.trim())
      }));
      
      const response = await fetch(`${API}/courses/${courseId}/quizzes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...quizData,
          questions: cleanedQuestions
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success("Quiz created successfully!");
        navigate(`/courses/${courseId}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create quiz");
      }
    } catch (error) {
      toast.error("Failed to create quiz");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'teacher' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Only teachers can create quizzes</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41]">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/courses/${courseId}`)}
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Course
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            Create Quiz
          </h1>
          {course && (
            <p className="text-[#588157]">For course: {course.title}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Quiz Details */}
          <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <h2 className="text-lg font-semibold text-[#344E41] mb-4">Quiz Details</h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-[#344E41]">Quiz Title *</Label>
                <Input
                  value={quizData.title}
                  onChange={(e) => setQuizData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Chapter 1 Review Quiz"
                  className="mt-1 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                  data-testid="quiz-title-input"
                />
              </div>
              
              <div>
                <Label className="text-[#344E41]">Description (Optional)</Label>
                <Textarea
                  value={quizData.description}
                  onChange={(e) => setQuizData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of what this quiz covers..."
                  className="mt-1 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#344E41]">Passing Score (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={quizData.passing_score}
                    onChange={(e) => setQuizData(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 70 }))}
                    className="mt-1 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                  />
                </div>
                <div>
                  <Label className="text-[#344E41]">Time Limit (minutes, optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={quizData.time_limit_minutes || ""}
                    onChange={(e) => setQuizData(prev => ({ ...prev, time_limit_minutes: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="No limit"
                    className="mt-1 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                  />
                </div>
              </div>
              
              {course?.content && (
                <div>
                  <Label className="text-[#344E41]">Link to Lesson (Optional)</Label>
                  <select
                    value={quizData.lesson_index ?? ""}
                    onChange={(e) => setQuizData(prev => ({ ...prev, lesson_index: e.target.value ? parseInt(e.target.value) : null }))}
                    className="mt-1 w-full p-2 bg-white border border-[#CCD5AE] rounded-md focus:border-[#3A5A40] focus:outline-none"
                  >
                    <option value="">Not linked to any lesson</option>
                    {course.content.map((lesson, index) => (
                      <option key={index} value={index}>
                        Lesson {index + 1}: {lesson.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#344E41]">Questions ({quizData.questions.length})</h2>
              <Button
                type="button"
                onClick={handleAddQuestion}
                className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Question
              </Button>
            </div>
            
            {quizData.questions.map((question, qIndex) => (
              <Card key={qIndex} className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-5 h-5 text-[#A3B18A]" />
                    <span className="text-sm font-medium text-[#588157]">Question {qIndex + 1}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-[#A3B18A]">Points:</Label>
                      <Input
                        type="number"
                        min="1"
                        value={question.points}
                        onChange={(e) => handleQuestionChange(qIndex, 'points', parseInt(e.target.value) || 1)}
                        className="w-16 h-8 text-sm bg-white border-[#CCD5AE]"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveQuestion(qIndex)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#344E41]">Question *</Label>
                    <Textarea
                      value={question.question}
                      onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                      placeholder="Enter your question..."
                      className="mt-1 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                      rows={2}
                      data-testid={`question-${qIndex}-text`}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-[#344E41] mb-2 block">Answer Options (click to mark correct)</Label>
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleQuestionChange(qIndex, 'correct_answer', oIndex)}
                            className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                              question.correct_answer === oIndex
                                ? 'bg-[#3A5A40] border-[#3A5A40] text-white'
                                : 'border-[#CCD5AE] hover:border-[#3A5A40] text-[#CCD5AE]'
                            }`}
                            data-testid={`question-${qIndex}-option-${oIndex}-correct`}
                          >
                            {question.correct_answer === oIndex && <CheckCircle2 className="w-5 h-5" />}
                            {question.correct_answer !== oIndex && <span className="text-sm">{String.fromCharCode(65 + oIndex)}</span>}
                          </button>
                          <Input
                            value={option}
                            onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                            placeholder={`Option ${String.fromCharCode(65 + oIndex)}`}
                            className="flex-1 bg-white border-[#CCD5AE] focus:border-[#3A5A40]"
                            data-testid={`question-${qIndex}-option-${oIndex}`}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#A3B18A] mt-2">
                      Click the circle to mark the correct answer. Leave unused options empty.
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/courses/${courseId}`)}
              className="border-[#CCD5AE] text-[#588157] hover:bg-[#E9EDC9]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#3A5A40] text-white hover:bg-[#344E41]"
              data-testid="create-quiz-submit"
            >
              {loading ? 'Creating...' : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Create Quiz
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
